import React, { useState, useEffect, useContext } from 'react';
import { LoginContext } from '../../reactcontext/ReactContext';
import axios from 'axios';
import { FaPlus, FaEdit, FaTrash, FaKey, FaTimes } from 'react-icons/fa';

const EMPTY_FORM = {
  name: '', displayName: '', description: '',
  module: '', subModule: '', action: 'read', order: 0
};

const PermissionManagement = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const { loginInfo } = useContext(LoginContext);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPermission, setEditingPermission] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, id: null, name: '' });
  const [successPopup, setSuccessPopup] = useState({ show: false, message: '' });

  const actions = ['create', 'read', 'update', 'delete', 'manage'];

  useEffect(() => {
    if (loginInfo.login && loginInfo.userDetails?.role?.toLowerCase() === 'admin') {
      fetchPermissions();
    }
  }, [loginInfo]);

  const fetchPermissions = async () => {
    try {
      const response = await axios.get(`${apiUrl}/permission`, {
        headers: { loginToken: localStorage.getItem('loginToken') }
      });
      setPermissions(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching permissions:', error);
      setLoading(false);
    }
  };

  const showSuccess = (message) => {
    setSuccessPopup({ show: true, message });
    setTimeout(() => {
      setSuccessPopup({ show: false, message: '' });
      fetchPermissions();
    }, 2500);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingPermission(null);
    setFormData(EMPTY_FORM);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPermission) {
        await axios.put(`${apiUrl}/permission/${editingPermission.id}`, formData, {
          headers: { loginToken: localStorage.getItem('loginToken') }
        });
        closeModal();
        showSuccess('Permission updated successfully!');
      } else {
        await axios.post(`${apiUrl}/permission/create`, formData, {
          headers: { loginToken: localStorage.getItem('loginToken') }
        });
        closeModal();
        showSuccess('Permission created successfully!');
      }
    } catch (error) {
      console.error('Error saving permission:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Failed to save permission';
      alert(errorMessage);
    }
  };

  const handleEdit = (permission) => {
    setEditingPermission(permission);
    setFormData({
      name: permission.name,
      displayName: permission.displayName,
      description: permission.description || '',
      module: permission.module,
      subModule: permission.subModule || '',
      action: permission.action,
      order: permission.order
    });
    setShowModal(true);
  };

  const handleDelete = (id, name) => {
    setDeleteConfirm({ show: true, id, name });
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${apiUrl}/permission/${deleteConfirm.id}`, {
        headers: { loginToken: localStorage.getItem('loginToken') }
      });
      setDeleteConfirm({ show: false, id: null, name: '' });
      showSuccess('Permission deleted successfully!');
    } catch (error) {
      console.error('Error deleting permission:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Failed to delete permission';
      setDeleteConfirm({ show: false, id: null, name: '' });
      alert(errorMessage);
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'create': return 'bg-green-100 text-green-800';
      case 'read':   return 'bg-blue-100 text-blue-800';
      case 'update': return 'bg-yellow-100 text-yellow-800';
      case 'delete': return 'bg-red-100 text-red-800';
      case 'manage': return 'bg-purple-100 text-purple-800';
      default:       return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) return <div className="text-center py-8">Loading...</div>;

  return (
    <div className="w-full min-h-screen bg-gray-50">
      <div className="w-full px-4 sm:px-6 lg:px-8 py-8">

        {/* Header */}
        <div className="mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 flex items-center">
              <FaKey className="mr-4 text-green-600 text-3xl" />
              Permission Management
            </h1>
            <p className="text-gray-600 mt-2">Define and manage system permissions</p>
          </div>
          <button
            onClick={() => { setEditingPermission(null); setFormData(EMPTY_FORM); setShowModal(true); }}
            className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white px-6 py-3 rounded-lg flex items-center shadow-md hover:shadow-lg transition-all duration-200 whitespace-nowrap"
          >
            <FaPlus className="mr-2" /> Add Permission
          </button>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gradient-to-r from-gray-50 to-gray-100 border-b-2 border-gray-200">
                  {['Name','Display Name','Module','Sub Module','Action','Actions'].map((h, i) => (
                    <th key={h} className={`px-6 py-4 text-xs font-semibold text-gray-700 uppercase tracking-widest ${i === 5 ? 'text-center' : 'text-left'}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {permissions.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-8 py-12 text-center text-gray-500">
                      No permissions found. Create your first permission to get started.
                    </td>
                  </tr>
                ) : permissions.map((permission, index) => (
                  <tr key={permission.id} className={`hover:bg-green-50 transition-colors duration-150 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                    <td className="px-6 py-3 text-sm">
                      <code className="bg-gray-100 px-2 py-1 rounded text-xs font-mono">{permission.name}</code>
                    </td>
                    <td className="px-6 py-3 text-sm font-semibold text-gray-900">{permission.displayName}</td>
                    <td className="px-6 py-3 text-sm">
                      <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold">{permission.module}</span>
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-600">
                      {permission.subModule
                        ? <span className="px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-xs font-medium">{permission.subModule}</span>
                        : <span className="text-gray-400 italic">—</span>}
                    </td>
                    <td className="px-6 py-3">
                      <span className={`inline-block px-3 py-1 text-xs font-semibold rounded-full ${getActionColor(permission.action)}`}>
                        {permission.action.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-sm">
                      <div className="flex justify-center gap-2">
                        <button onClick={() => handleEdit(permission)} className="text-blue-600 hover:bg-blue-100 p-2 rounded-lg transition-all duration-150" title="Edit">
                          <FaEdit size={16} />
                        </button>
                        <button onClick={() => handleDelete(permission.id, permission.name)} className="text-red-600 hover:bg-red-100 p-2 rounded-lg transition-all duration-150" title="Delete">
                          <FaTrash size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Add / Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh]">

            {/* Sticky Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
              <h2 className="text-xl font-bold text-gray-800">
                {editingPermission ? 'Edit Permission' : 'Add New Permission'}
              </h2>
              <button
                type="button"
                onClick={closeModal}
                className="p-2 rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-800 transition-colors"
                title="Close"
              >
                <FaTimes size={18} />
              </button>
            </div>

            {/* Scrollable Body */}
            <div className="overflow-y-auto flex-1 px-6 py-5">
              <form onSubmit={handleSubmit} id="perm-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 text-sm font-semibold mb-1">Name <span className="text-red-500">*</span></label>
                    <input type="text" value={formData.name} required placeholder="e.g., users.view"
                      onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                      className="border rounded-lg w-full py-2 px-3 text-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-green-400" />
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-semibold mb-1">Display Name <span className="text-red-500">*</span></label>
                    <input type="text" value={formData.displayName} required placeholder="e.g., View Users"
                      onChange={e => setFormData(p => ({ ...p, displayName: e.target.value }))}
                      className="border rounded-lg w-full py-2 px-3 text-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-green-400" />
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-semibold mb-1">Module <span className="text-red-500">*</span></label>
                    <input type="text" value={formData.module} required placeholder="e.g., Users"
                      onChange={e => setFormData(p => ({ ...p, module: e.target.value }))}
                      className="border rounded-lg w-full py-2 px-3 text-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-green-400" />
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-semibold mb-1">Sub Module</label>
                    <input type="text" value={formData.subModule} placeholder="e.g., User Management (optional)"
                      onChange={e => setFormData(p => ({ ...p, subModule: e.target.value }))}
                      className="border rounded-lg w-full py-2 px-3 text-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-green-400" />
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-semibold mb-1">Action</label>
                    <select value={formData.action} onChange={e => setFormData(p => ({ ...p, action: e.target.value }))}
                      className="border rounded-lg w-full py-2 px-3 text-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-green-400">
                      {actions.map(a => <option key={a} value={a}>{a}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-semibold mb-1">Order</label>
                    <input type="number" value={formData.order} min="0"
                      onChange={e => setFormData(p => ({ ...p, order: parseInt(e.target.value) || 0 }))}
                      className="border rounded-lg w-full py-2 px-3 text-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-green-400" />
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-gray-700 text-sm font-semibold mb-1">Description</label>
                  <textarea value={formData.description} rows="2" placeholder="Optional description"
                    onChange={e => setFormData(p => ({ ...p, description: e.target.value }))}
                    className="border rounded-lg w-full py-2 px-3 text-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 resize-none" />
                </div>
              </form>
            </div>

            {/* Sticky Footer */}
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-xl shrink-0">
              <button type="button" onClick={closeModal}
                className="px-5 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold text-sm transition-colors">
                Cancel
              </button>
              <button type="submit" form="perm-form"
                className="px-5 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white font-semibold text-sm transition-colors">
                {editingPermission ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirm.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-bold text-gray-800">Confirm Delete</h3>
              <button onClick={() => setDeleteConfirm({ show: false, id: null, name: '' })}
                className="p-1.5 rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-800 transition-colors">
                <FaTimes size={16} />
              </button>
            </div>
            <div className="px-6 py-6 flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <FaTrash className="text-red-600" size={20} />
              </div>
              <div>
                <p className="text-gray-800 font-medium">
                  Delete permission <span className="font-bold text-red-600">"{deleteConfirm.name}"</span>?
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  This will remove the permission from all roles that currently have it assigned.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 rounded-b-xl">
              <button onClick={() => setDeleteConfirm({ show: false, id: null, name: '' })}
                className="px-5 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold text-sm transition-colors">
                Cancel
              </button>
              <button onClick={confirmDelete}
                className="px-5 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white font-semibold text-sm flex items-center gap-2 transition-colors">
                <FaTrash size={13} /> Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Popup */}
      {successPopup.show && (
        <div className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none">
          <div className="bg-white rounded-xl shadow-2xl px-8 py-6 flex flex-col items-center gap-3 border border-green-100">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-800">{successPopup.message}</h3>
            <p className="text-sm text-gray-500">Refreshing list...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PermissionManagement;
